// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Search, Button } from '@carbon/react';
import { ArrowLeft } from '@carbon/icons-react';
import { useDigitalTwin } from '../../hooks/useDigitalTwin';
import { useUrlState } from '../../hooks/useUrlState';
import { TWIN_POLL_INTERVAL_MS } from '../../config/queryConfig';
import {
  getEntityDisplayType,
  getEntityCode,
  groupMeasurements,
  isAssetEntityType,
  formatAttributeValue,
  aggregateTelemetryState,
  buildTelemetryRecencyMap,
  isTelemetryLive,
  aggregateChildStatus,
  buildTelemetryChildIndex,
  categorizeStatus,
  stripOntologyPrefix,
} from '../../utils/entityHelpers';
import type { GroupedMeasurements } from '../../utils/entityHelpers';
import { buildHierarchy } from '../../utils/entityHierarchy';
import { buildPowerFlowGraph } from '../../utils/powerFlowGraph';
import { isBusTerminalType } from '../../config/powerFlowSymbols';
import { assetName, deriveUnitFullLabel } from '../../utils/entityLabels';
import type { HierarchyNode } from '../../utils/entityHierarchy';
import type { Entity } from '../../types/digitalTwin';
import { EntitySchemas } from '../../config/entity-schemas';
import { Loading } from '../../components/shell/ScreenHelpers';
import { AssetTypeChips } from '../../components/shell/AssetTypeChips';
import { domainOfEntityType, domainRank } from '../../utils/assetDomain';
import { useSiteTime } from '../../hooks/useSiteTime';
import type { SiteTimeValue } from '../../contexts/SiteTimeContext';
import './DCAssets.css';

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

/**
 * Live / Unknown indicator driven by telemetry recency. An asset is "Live"
 * (green pulsing dot) when it has telemetry within TELEMETRY_LIVE_WINDOW_MS,
 * otherwise "Unknown" (neutral dot).
 */
function LiveIndicator({ live }: { live: boolean }) {
  return (
    <span className={`dca-pill ${live ? 'dca-live' : 'dca-unknown'}`}>
      <span className={`dca-pill-dot ${live ? 'dca-live-dot' : ''}`} />
      {live ? 'Live' : 'Unknown'}
    </span>
  );
}

/** Format an ISO timestamp for display, guarding against bad/empty values. */
function formatStamp(value: unknown, format: SiteTimeValue['format']): string | null {
  if (!value || typeof value !== 'string') return null;
  return format(value, 'dateTime', '') || null;
}

// ---------------------------------------------------------------------------
// Asset Directory table (list view, EnergyFluo v1 design)
// ---------------------------------------------------------------------------

type Tone = 'ok' | 'info' | 'warn' | 'fault' | 'pend';

/** One directory row. Comm is resolved at render time: it moves with the clock. */
interface AssetRow {
  id: string;
  eqId: string;
  type: string;
  domain: string;
  mm: string;
  statusLabel: string;
  statusTone: Tone;
}

/** Status column from the entity's own status, else its worst telemetry point's. */
function statusOf(status: string | undefined): { label: string; tone: Tone } {
  if (!status) return { label: 'Unknown', tone: 'pend' };
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  switch (categorizeStatus(status)) {
    case 'healthy':
      return { label, tone: 'ok' };
    case 'warning':
      return { label, tone: 'warn' };
    case 'alarming':
      return { label, tone: 'fault' };
    case 'offline':
      return { label, tone: 'warn' };
    default:
      return { label, tone: 'pend' };
  }
}

const attrText = (entity: Entity, key: string): string => {
  const value = entity.attributes?.[key];
  return typeof value === 'string' && value.trim() && value.trim() !== '—' ? value.trim() : '';
};

/** A Comm / Status badge: dot for good news, warning glyph for bad. */
function StateBadge({ tone, label }: { tone: Tone; label: string }) {
  return (
    <span className={`dca-badge dca-badge--${tone}`}>
      {tone === 'warn' || tone === 'fault' ? (
        <svg
          width="11"
          height="11"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="M8 1.8L15 14H1z" strokeLinejoin="round" />
          <path d="M8 6.4v3.4M8 11.6v.4" strokeLinecap="round" />
        </svg>
      ) : tone !== 'pend' ? (
        <span className="dca-badge-dot" aria-hidden="true" />
      ) : null}
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Dependency item component (Feeds / Fed by rows in detail view)
// ---------------------------------------------------------------------------

interface DependencyItemProps {
  entity: Entity;
  live: boolean;
  onClick: () => void;
}

function DependencyItem({ entity, live, onClick }: DependencyItemProps) {
  const label = assetName(entity, entity.id);

  return (
    <div
      className="dca-row dca-feed-row"
      role="button"
      data-testid="asset-dependency"
      data-asset-id={entity.id}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <span className="dca-code">{getEntityCode(entity.type)}</span>
      <span className="dca-row-info">
        <span className="dca-row-name">{label}</span>
        <span className="dca-row-type">{getEntityDisplayType(entity.type)}</span>
      </span>
      <LiveIndicator live={live} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function DCAssets() {
  const { format } = useSiteTime();
  const { site_id } = useParams();
  const { entities, relationships, loading, error, refetch } = useDigitalTwin(site_id, {
    pollIntervalMs: TWIN_POLL_INTERVAL_MS,
  });

  const [urlState, patchUrl] = useUrlState({ q: '', domain: 'all', asset: '' });
  const { q: searchQuery, domain: domainFilter } = urlState;
  const selectedEntityId = urlState.asset || null;
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const assetEntities = useMemo(
    () => entities.filter(e => isAssetEntityType(e.type) || isBusTerminalType(e.type)),
    [entities]
  );
  const roots = useMemo(
    () => buildHierarchy(assetEntities, relationships),
    [assetEntities, relationships]
  );

  const recency = useMemo(
    () => buildTelemetryRecencyMap(entities, relationships),
    [entities, relationships]
  );
  const liveIds = useMemo(() => {
    const ids = new Set<string>();
    recency.forEach((t, id) => {
      if (isTelemetryLive(t, now)) ids.add(id);
    });
    return ids;
  }, [recency, now]);

  const rows = useMemo<AssetRow[]>(() => {
    const byId = new Map(entities.map(e => [e.id, e]));
    const childIndex = buildTelemetryChildIndex(relationships);
    const out: AssetRow[] = [];
    const walk = (nodes: HierarchyNode[]) => {
      for (const node of nodes) {
        const entity = node.entity;
        const bare = stripOntologyPrefix(entity.type);
        if (bare !== 'SiteType' && bare !== 'Site' && !isBusTerminalType(entity.type)) {
          const status = statusOf(
            aggregateChildStatus(entity, entities, relationships, byId, childIndex)
          );
          out.push({
            id: entity.id,
            eqId: assetName(entity, entity.id),
            type: node.displayType,
            domain: domainOfEntityType(entity.type),
            mm:
              [attrText(entity, 'manufacturer'), attrText(entity, 'model')]
                .filter(Boolean)
                .join(' · ') || '—',
            statusLabel: status.label,
            statusTone: status.tone,
          });
        }
        walk(node.children);
      }
    };
    walk(roots);
    return out;
  }, [roots, entities, relationships]);

  const searched = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      [r.eqId, r.type, r.mm, r.statusLabel, r.domain, r.id].join(' ').toLowerCase().includes(q)
    );
  }, [rows, searchQuery]);
  const domainChips = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(r.domain, 0);
    for (const r of searched) counts.set(r.domain, (counts.get(r.domain) ?? 0) + 1);
    return Array.from(counts, ([type, count]) => ({ type, count })).sort(
      (a, b) => domainRank(a.type) - domainRank(b.type) || a.type.localeCompare(b.type)
    );
  }, [rows, searched]);
  const activeDomain = domainChips.some(c => c.type === domainFilter) ? domainFilter : 'all';
  const visibleRows = useMemo(
    () => (activeDomain === 'all' ? searched : searched.filter(r => r.domain === activeDomain)),
    [searched, activeDomain]
  );

  const selectedEntity = useMemo(
    () => (selectedEntityId ? entities.find(e => e.id === selectedEntityId) : null),
    [selectedEntityId, entities]
  );

  const selectedAggregatedState = useMemo(
    () =>
      selectedEntity ? aggregateTelemetryState(selectedEntity, entities, relationships) : undefined,
    [selectedEntity, entities, relationships]
  );

  const selectedGroupedMeasurements = useMemo<GroupedMeasurements[]>(
    () => groupMeasurements(selectedAggregatedState),
    [selectedAggregatedState]
  );

  const flowGraph = useMemo(
    () => buildPowerFlowGraph(entities, relationships),
    [entities, relationships]
  );
  const flowNeighbours = selectedEntityId
    ? (flowGraph.verticesById.get(selectedEntityId) ?? flowGraph.adjacency.get(selectedEntityId))
    : undefined;
  const feedsEntities = useMemo(() => {
    const ids = new Set(flowNeighbours?.out ?? []);
    return entities.filter(e => ids.has(e.id));
  }, [flowNeighbours, entities]);
  const fedByEntities = useMemo(() => {
    const ids = new Set(flowNeighbours?.in ?? []);
    return entities.filter(e => ids.has(e.id));
  }, [flowNeighbours, entities]);

  const navigateToEntity = useCallback(
    (entityId: string) => patchUrl({ asset: entityId }, { push: true }),
    [patchUrl]
  );

  const toggleTelemetryGroup = useCallback((group: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  }, []);

  if (loading) {
    return (
      <div className="dca-state">
        <Loading label="Loading assets…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dca-state">
        <p className="dca-state-title">Failed to load asset data</p>
        <p className="dca-state-detail">{error}</p>
        <Button kind="tertiary" size="sm" onClick={refetch}>
          Retry
        </Button>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Detail view
  // -------------------------------------------------------------------------
  if (selectedEntity) {
    const displayType = getEntityDisplayType(selectedEntity.type);
    const stamp = formatStamp(selectedAggregatedState?.updatedAt, format);
    const tierRaw = selectedEntity.attributes?.tier ?? selectedEntity.attributes?.criticality;
    const tier = tierRaw != null ? formatAttributeValue(tierRaw) : '';
    const hasSOP = Boolean(selectedEntity.attributes?.hasSOP);
    const hasDoc = Boolean(selectedEntity.attributes?.hasDocument);
    const schemaFields = (EntitySchemas[selectedEntity.type]?.attributeFields ?? []).filter(
      field =>
        selectedEntity.attributes?.[field] != null &&
        field !== 'label' &&
        field !== 'upstreamId' &&
        field !== 'hasSOP' &&
        field !== 'hasDocument'
    );
    const title = deriveUnitFullLabel(selectedEntity);
    const description = selectedEntity.attributes?.label?.trim();
    const isMonoField = (f: string) => /id|serial/i.test(f);

    return (
      <div className="dca-detail-page" data-testid="asset-detail">
        <button type="button" className="dca-back" onClick={() => patchUrl({ asset: null })}>
          <ArrowLeft size={16} />
          All assets
        </button>

        <div className="dca-detail-head">
          <div className="dca-detail-head-main">
            <h1 className="dca-detail-title" data-testid="asset-detail-title">
              {title}
            </h1>
            <div className="dca-detail-type">{displayType}</div>
            {tier && <div className="dca-detail-tier">{tier}</div>}
          </div>
          <div className="dca-detail-head-side">
            <LiveIndicator live={liveIds.has(selectedEntity.id)} />
            {stamp && <span className="dca-stamp">{stamp}</span>}
          </div>
        </div>

        {/* Entity info */}
        <div className="dca-section-label">Entity info</div>
        <div className="dca-list-card">
          <div className="dca-entity-grid">
            <span className="dca-entity-k">Type</span>
            <span className="dca-entity-v">{displayType}</span>
            {description && description !== title && (
              <>
                <span className="dca-entity-k">Description</span>
                <span className="dca-entity-v">{description}</span>
              </>
            )}
            <span className="dca-entity-k">Entity ID</span>
            <span className="dca-entity-v dca-mono">{selectedEntity.id}</span>
            {schemaFields.map(field => (
              <React.Fragment key={field}>
                <span className="dca-entity-k">{field}</span>
                <span className={`dca-entity-v ${isMonoField(field) ? 'dca-mono' : ''}`}>
                  {formatAttributeValue(selectedEntity.attributes[field])}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Real-time telemetry */}
        <div className="dca-section-label">Real-time telemetry</div>
        {selectedGroupedMeasurements.length > 0 ? (
          <div className="dca-tel-groups">
            {selectedGroupedMeasurements.map(group => {
              const expanded = !collapsedGroups.has(group.group);
              return (
                <div key={group.group} className="dca-tel-group">
                  <button
                    type="button"
                    className="dca-tel-header"
                    onClick={() => toggleTelemetryGroup(group.group)}
                  >
                    <span className={`dca-arrow ${expanded ? 'dca-arrow-open' : ''}`}>&#9656;</span>
                    <span className="dca-tel-name">{group.group}</span>
                    <span className="dca-tel-count">{group.entries.length}</span>
                  </button>
                  {expanded && (
                    <div className="dca-tel-body">
                      {group.entries.map(m => (
                        <div key={m.rawKey} className="dca-tel-row">
                          <span className="dca-tel-k">{m.key}</span>
                          <span className="dca-tel-v">{m.display}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="dca-empty-box">No measurements available</div>
        )}
        {stamp && <div className="dca-tel-updated">Last updated: {stamp}</div>}

        {/* Standard operating procedures */}
        <div className="dca-section-label">Standard operating procedures</div>
        {hasSOP || hasDoc ? (
          <div className="dca-list-card">
            {hasSOP && (
              <div className="dca-sop-row">
                <span className="dca-sop-id">SOP</span>
                <span className="dca-sop-label">SOP available</span>
              </div>
            )}
            {hasDoc && (
              <div className="dca-sop-row">
                <span className="dca-sop-id">DOC</span>
                <span className="dca-sop-label">Documentation available</span>
              </div>
            )}
          </div>
        ) : (
          <div className="dca-empty-box">No SOPs linked to this asset.</div>
        )}

        {/* Feeds (downstream) */}
        <div className="dca-section-label">Feeds ({feedsEntities.length})</div>
        {feedsEntities.length > 0 ? (
          <div className="dca-list-card">
            {feedsEntities.map(entity => (
              <DependencyItem
                key={entity.id}
                entity={entity}
                live={liveIds.has(entity.id)}
                onClick={() => navigateToEntity(entity.id)}
              />
            ))}
          </div>
        ) : (
          <div className="dca-empty-box">Nothing downstream of this asset.</div>
        )}

        {/* Fed by (upstream) */}
        <div className="dca-section-label">Fed by ({fedByEntities.length})</div>
        {fedByEntities.length > 0 ? (
          <div className="dca-list-card">
            {fedByEntities.map(entity => (
              <DependencyItem
                key={entity.id}
                entity={entity}
                live={liveIds.has(entity.id)}
                onClick={() => navigateToEntity(entity.id)}
              />
            ))}
          </div>
        ) : (
          <div className="dca-empty-box">No feeding source recorded.</div>
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // List view
  // -------------------------------------------------------------------------
  return (
    <div className="dca-list-page" data-testid="asset-directory">
      <div className="dca-list-head">
        <h1 className="dca-h1">Asset Directory</h1>
      </div>

      <div className="dca-search">
        <Search
          id="asset-search"
          size="md"
          labelText="Search assets"
          placeholder="Search by ID, type, or manufacturer…"
          value={searchQuery}
          onChange={e => patchUrl({ q: e.target.value })}
          onClear={() => patchUrl({ q: null })}
        />
      </div>

      <AssetTypeChips
        label="Filter by domain"
        chips={domainChips}
        total={searched.length}
        value={activeDomain}
        onChange={domain => patchUrl({ domain: domain === 'all' ? null : domain })}
      />

      <div className="dca-table-wrap">
        <table className="dca-table">
          <thead>
            <tr>
              <th scope="col">Eq. ID</th>
              <th scope="col">Type</th>
              <th scope="col">Manufacturer / Model</th>
              <th scope="col">Comm</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(row => {
              const hasTelemetry = recency.has(row.id);
              const online = liveIds.has(row.id);
              return (
                <tr
                  key={row.id}
                  className={selectedEntityId === row.id ? 'dca-tr-active' : undefined}
                  data-testid="asset-row"
                  data-asset-id={row.id}
                  tabIndex={0}
                  onClick={() => navigateToEntity(row.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigateToEntity(row.id);
                    }
                  }}
                >
                  <td className="dca-td-id">{row.eqId}</td>
                  <td>{row.type}</td>
                  <td className="dca-td-muted">{row.mm}</td>
                  <td>
                    <StateBadge
                      tone={!hasTelemetry ? 'pend' : online ? 'ok' : 'warn'}
                      label={!hasTelemetry ? 'No telemetry' : online ? 'Online' : 'Offline'}
                    />
                  </td>
                  <td>
                    <StateBadge tone={row.statusTone} label={row.statusLabel} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {visibleRows.length === 0 && (
          <div className="dca-empty-row">No assets match your search or filter.</div>
        )}
      </div>
    </div>
  );
}

export default DCAssets;
